import React, { useMemo, useState } from 'react';

import { Box, CircularProgress, Typography } from '@material-ui/core';
import { FiArrowLeft, FiDownload, FiFileText } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';

import { getStaffRequest } from '../../api/companies';
import { TeamsReportClientApp, TeamsReportRequest } from '../../api/reports/teamsReports';
import { AppButton } from '../../components/AppButton';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { SettingsStatusPill } from '../../components/SettingsCard/SettingsStatusPill';
import { usePermissions } from '../../hooks/usePermissions';
import { Team, TEAMS_PERMISSIONS } from '../../types';
import { RequestReportDialog } from './components/RequestReportDialog';
import { formatReportDate, ReportRow, STATUS_TONES } from './components/ReportRow';
import {
  ANALYSIS_LABELS,
  INDIVIDUAL_SECTIONS,
  REPORTS_ROUTE,
  REPORT_TYPE_LABELS,
  SNAPSHOT_STATS,
  STATUS_LABELS,
} from './constants';
import { useReportDownload } from './hooks/useReportDownload';
import { useReportsHistory } from './hooks/useReportsHistory';
import { useReportStatusPolling } from './hooks/useReportStatusPolling';
import { useReportStyles } from './styles';
import { formatReportSize, getReportErrorMessage, getReportTitle, getScopeLabel } from './utils';

export interface TeamsReportsProps {
  companyId?: string;
  companyName?: string;
  /** Decides which app the notification links open. */
  clientApp: TeamsReportClientApp;
  currentUserId?: string;
  teams: Team[];
  getStaff?: typeof getStaffRequest;
}

const useCanDelete = (currentUserId?: string) => {
  const { can, isOwner, isInternalAdmin } = usePermissions();
  // Company-wide holders may delete any report; the gateway has the final word (403 for recipients)
  const companyWide = isInternalAdmin || isOwner || can(TEAMS_PERMISSIONS.CAN_GENERATE_REPORTS);
  return (report: TeamsReportRequest) => companyWide || report.requested_by.user_id === currentUserId;
};

const ErrorBox: React.FC<{ title: string; description?: string }> = ({ title, description }) => {
  const classes = useReportStyles();
  return (
    <Box className={classes.alert} role="alert">
      <Typography className={classes.alertTitle}>{title}</Typography>
      {description && <Typography className={classes.alertText}>{description}</Typography>}
    </Box>
  );
};

/* ---------- /teams/reports ---------- */

export const TeamsReportsHistory: React.FC<TeamsReportsProps> = props => {
  const classes = useReportStyles();
  const { companyId, currentUserId } = props;
  const history = useReportsHistory(companyId);
  const { download, downloadingId, error: downloadError } = useReportDownload(companyId, history.refresh);
  const canDelete = useCanDelete(currentUserId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<TeamsReportRequest | null>(null);

  return (
    <Box className={classes.page}>
      <Box className={classes.pageHead}>
        <Box>
          <Typography className={classes.pageTitle}>Reports</Typography>
          <Typography className={classes.hint}>
            Reports requested in the last 30 days. Files are deleted after 30 days.
          </Typography>
        </Box>
        <AppButton
          variant="contained"
          color="primary"
          startIcon={<FiFileText size={15} />}
          onClick={() => setDialogOpen(true)}>
          Request report
        </AppButton>
      </Box>

      {downloadError && <ErrorBox title={downloadError.title} description={downloadError.description} />}

      {history.loading ? (
        <Box display="flex" justifyContent="center" padding={6}>
          <CircularProgress size={32} />
        </Box>
      ) : history.loadError ? (
        <ErrorBox title="We couldn’t load your reports" description={history.loadError} />
      ) : !history.items.length ? (
        <Box className={clsx(classes.stackCard, classes.emptyCard)}>
          <Typography className={classes.statusTitle}>No reports yet</Typography>
          <Typography className={classes.hint}>
            Request a company snapshot or an individual report — it will show up here when it’s ready.
          </Typography>
        </Box>
      ) : (
        <>
          {history.items.map(report => (
            <ReportRow
              key={report.request_id}
              report={report}
              canDelete={canDelete(report)}
              downloading={downloadingId === report.request_id}
              deleting={history.deletingId === report.request_id}
              onDownload={() => download(report.request_id)}
              onDelete={() => setConfirmDelete(report)}
              onRequestAgain={() => setDialogOpen(true)}
            />
          ))}
          {history.canShowMore && (
            <Box display="flex" justifyContent="center">
              <AppButton variant="outlined" onClick={history.showMore}>
                Show more
              </AppButton>
            </Box>
          )}
        </>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete this report?"
        subtitle="It disappears from the Reports page and its download link stops working. This can’t be undone."
        confirmText="Delete"
        cancelText="Cancel"
        handleClose={() => setConfirmDelete(null)}
        handleConfirm={() => {
          if (confirmDelete) history.remove(confirmDelete.request_id);
          setConfirmDelete(null);
        }}
      />

      <RequestReportDialog
        {...props}
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          history.refresh();
        }}
      />
    </Box>
  );
};

/* ---------- /teams/reports/:requestId — where the notification links land ---------- */

export const TeamsReportDetails: React.FC<TeamsReportsProps & { requestId: string }> = ({
  requestId,
  ...props
}) => {
  const classes = useReportStyles();
  const routerHistory = useHistory();
  const { companyId, clientApp } = props;
  const [reloadKey, setReloadKey] = useState(0);
  const { report: current, error, timedOut } = useReportStatusPolling(companyId, requestId, reloadKey);
  // A 409/410 on download means the status on screen is stale: read it again
  const { download, downloadingId, error: downloadError } = useReportDownload(companyId, () =>
    setReloadKey(key => key + 1)
  );

  const errorMessage = useMemo(() => {
    if (!error) return null;
    const status = (error as any)?.response?.status;
    if (status === 404) {
      return {
        title: 'Report not found',
        description:
          clientApp === 'legend'
            ? 'It may belong to another company — pick that company in the filter above — or it has been deleted.'
            : 'It may have been deleted, or it belongs to a company you’re not signed in to.',
      };
    }
    return getReportErrorMessage(error);
  }, [error, clientApp]);

  const sectionLabels = Object.fromEntries(INDIVIDUAL_SECTIONS.map(item => [item.value, item.label]));
  const statLabels = Object.fromEntries(SNAPSHOT_STATS.map(item => [item.value, item.label]));

  return (
    <Box className={classes.page}>
      <Box>
        <AppButton
          onClick={() => routerHistory.push(REPORTS_ROUTE)}
          variant="text"
          size="small"
          startIcon={<FiArrowLeft size={14} />}>
          All reports
        </AppButton>
      </Box>

      {errorMessage ? (
        <ErrorBox title={errorMessage.title} description={errorMessage.description} />
      ) : !current ? (
        <Box display="flex" justifyContent="center" padding={6}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box className={classes.stackCard}>
          <Box className={classes.pageHead}>
            <Box minWidth={0}>
              <Typography className={classes.pageTitle}>{getReportTitle(current)}</Typography>
              <Box display="flex" gridGap={8} marginTop={1} flexWrap="wrap">
                <SettingsStatusPill label={STATUS_LABELS[current.status]} tone={STATUS_TONES[current.status]} />
                {current.contains_pii && <SettingsStatusPill label="Contains personal data" tone="warning" />}
              </Box>
            </Box>
            {current.status === 'completed' && (
              <AppButton
                variant="contained"
                color="primary"
                startIcon={<FiDownload size={15} />}
                loading={downloadingId === current.request_id}
                loaderVariant="paper"
                onClick={() => download(current.request_id)}>
                Download report
              </AppButton>
            )}
          </Box>

          {(current.status === 'pending' || current.status === 'running') && (
            <Box display="flex" alignItems="center" gridGap={12}>
              <CircularProgress size={20} />
              <Typography className={classes.hint}>
                {timedOut
                  ? 'Taking longer than expected — check back on the Reports page.'
                  : 'Generating — this page updates by itself. It usually takes under a minute.'}
              </Typography>
            </Box>
          )}
          {current.status === 'completed' && current.error_code === 'analysis_failed' && (
            <Typography className={classes.hint}>Analysis unavailable — the report is complete without it.</Typography>
          )}
          {current.status === 'failed' && (
            <ErrorBox
              title="We couldn’t generate this report"
              description={`Request it again from the Reports page. Support reference: ${
                current.error_code || 'unknown'
              }.`}
            />
          )}
          {current.status === 'expired' && (
            <Typography className={classes.hint}>
              This report expired after 30 days and its file has been deleted. Request it again from the
              Reports page.
            </Typography>
          )}
          {downloadError && <ErrorBox title={downloadError.title} description={downloadError.description} />}

          <Box className={classes.detailGrid} marginTop={1}>
            <span className={classes.detailLabel}>Type</span>
            <span className={classes.detailValue}>{REPORT_TYPE_LABELS[current.report_type]}</span>
            <span className={classes.detailLabel}>Scope</span>
            <span className={classes.detailValue}>{getScopeLabel(current)}</span>
            {current.report_type === 'individual' ? (
              <>
                <span className={classes.detailLabel}>Sections</span>
                <span className={classes.detailValue}>
                  {current.sections.map(section => sectionLabels[section]).join(', ')}
                </span>
                <span className={classes.detailLabel}>Field set</span>
                <span className={classes.detailValue}>{current.field_set === 'full' ? 'Full details' : 'Lean'}</span>
              </>
            ) : (
              <>
                <span className={classes.detailLabel}>Included</span>
                <span className={classes.detailValue}>
                  {current.stats.map(stat => statLabels[stat]).join(', ')}
                </span>
              </>
            )}
            {!!current.scope_summary.locations.length && (
              <>
                <span className={classes.detailLabel}>Locations</span>
                <span className={classes.detailValue}>
                  {current.scope_summary.locations.map(location => location.name).join(', ')}
                </span>
              </>
            )}
            <span className={classes.detailLabel}>Analysis</span>
            <span className={classes.detailValue}>{ANALYSIS_LABELS[current.analysis_mode]}</span>
            <span className={classes.detailLabel}>Requested by</span>
            <span className={classes.detailValue}>
              {current.requested_by.name}
              {current.requested_by.actor_type === 'legend' ? ' (Vyce)' : ''} on{' '}
              {formatReportDate(current.created_at)}
            </span>
            {current.page_count != null && (
              <>
                <span className={classes.detailLabel}>File</span>
                <span className={classes.detailValue}>
                  {current.page_count} pages · {formatReportSize(current.size_bytes)}
                </span>
              </>
            )}
            <span className={classes.detailLabel}>Available until</span>
            <span className={classes.detailValue}>{formatReportDate(current.expires_at, false)}</span>
          </Box>
        </Box>
      )}
    </Box>
  );
};
